import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { map } from 'rxjs';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';

type Capability = {
  eyebrow: string;
  title: string;
  promise: string;
  lead: string;
  flow: string[];
  outcomes: string[];
  scene:
    | 'site'
    | 'content'
    | 'global'
    | 'team'
    | 'permission'
    | 'mailbox'
    | 'stock'
    | 'pos'
    | 'marketplace'
    | 'campaign'
    | 'automation';
  proof?: {
    eyebrow: string;
    title: string;
    lead: string;
    image: string;
    alt: string;
    points: string[];
  };
};

type CapabilityContext = {
  eyebrow: string;
  title: string;
  lead: string;
  items: Array<{ title: string; text: string }>;
};

type MailboxStory = {
  eyebrow: string;
  title: string;
  lead: string;
  channels: Array<{ label: string; title: string; text: string; badge: string }>;
  inboxEyebrow: string;
  inboxTitle: string;
  inboxLead: string;
  replyPlaceholder: string;
  send: string;
  filters: string[];
  threadList: Array<{ source: string; name: string; preview: string; state: string }>;
  activeThread: {
    customer: string;
    context: string;
    messages: Array<{ sender: string; text: string; mine?: boolean }>;
  };
  operationsEyebrow: string;
  operationsTitle: string;
  operations: Array<{ title: string; text: string }>;
};

@Component({
  selector: 'app-aymudo-capability-page',
  imports: [RouterLink],
  template: `
    <main class="capability-page" [attr.data-scene]="capability().scene">
      <section class="capability-hero">
        <nav>
          <a [routerLink]="i18n.routePath('products')">{{ labels().products }}</a
          ><span>→</span><strong>{{ capability().title }}</strong>
        </nav>
        <div class="hero-copy">
          <p>{{ capability().eyebrow }}</p>
          <h1>{{ capability().promise }}</h1>
          <span>{{ capability().lead }}</span>
          <div>
            <a class="primary" [routerLink]="i18n.routePath('subscribe')"
              >{{ labels().subscribe }} <b>↗</b></a
            ><a href="#nasil-calisir">{{ labels().seeHow }} <b>↓</b></a>
          </div>
        </div>
        <div class="system-scene" aria-hidden="true">
          <span class="scene-label">AYMUDO / {{ capability().title }}</span>
          <div class="scene-core">
            <i>A</i><strong>{{ labels().oneCore }}</strong>
          </div>
          @for (step of capability().flow; track step; let index = $index) {
            <article [style.--index]="index">
              <small>0{{ index + 1 }}</small
              ><strong>{{ step }}</strong
              ><i>{{ index + 1 === capability().flow.length ? '✓' : '→' }}</i>
            </article>
          }
        </div>
      </section>

      @if (capability().scene === 'mailbox') {
        <section class="mailbox-story">
          <header>
            <p>{{ mailboxStory().eyebrow }}</p>
            <h2>{{ mailboxStory().title }}</h2>
            <span>{{ mailboxStory().lead }}</span>
          </header>
          <div class="mailbox-channels">
            @for (channel of mailboxStory().channels; track channel.title; let index = $index) {
              <article>
                <small>0{{ index + 1 }} / {{ channel.label }}</small>
                <b>{{ channel.badge }}</b>
                <h3>{{ channel.title }}</h3>
                <p>{{ channel.text }}</p>
              </article>
            }
          </div>
        </section>

        <section class="unified-inbox">
          <header>
            <p>{{ mailboxStory().inboxEyebrow }}</p>
            <h2>{{ mailboxStory().inboxTitle }}</h2>
            <span>{{ mailboxStory().inboxLead }}</span>
          </header>
          <div class="inbox-window">
            <aside>
              <div class="inbox-brand"><i>A</i><strong>AYMUDO</strong></div>
              <nav>
                @for (filter of mailboxStory().filters; track filter; let first = $first) {
                  <span [class.active]="first"
                    >{{ filter }}
                    @if (first) {
                      <b>12</b>
                    }
                  </span>
                }
              </nav>
            </aside>
            <div class="thread-list">
              <header>
                <strong>{{ mailboxStory().inboxEyebrow }}</strong
                ><b>＋</b>
              </header>
              @for (thread of mailboxStory().threadList; track thread.name; let first = $first) {
                <article [class.active]="first">
                  <small>{{ thread.source }}</small>
                  <strong>{{ thread.name }}</strong>
                  <p>{{ thread.preview }}</p>
                  <span>{{ thread.state }}</span>
                </article>
              }
            </div>
            <div class="thread-detail">
              <header>
                <div>
                  <strong>{{ mailboxStory().activeThread.customer }}</strong>
                  <small>{{ mailboxStory().activeThread.context }}</small>
                </div>
                <span>•••</span>
              </header>
              <div class="thread-messages">
                @for (message of mailboxStory().activeThread.messages; track message.text) {
                  <article [class.mine]="message.mine">
                    <small>{{ message.sender }}</small>
                    <p>{{ message.text }}</p>
                  </article>
                }
              </div>
              <footer>
                <span>↳</span>
                <p>{{ mailboxStory().replyPlaceholder }}</p>
                <b>{{ mailboxStory().send }} ↑</b>
              </footer>
            </div>
          </div>
        </section>

        <section class="mailbox-operations">
          <header>
            <p>{{ mailboxStory().operationsEyebrow }}</p>
            <h2>{{ mailboxStory().operationsTitle }}</h2>
          </header>
          <div>
            @for (item of mailboxStory().operations; track item.title; let index = $index) {
              <article>
                <small>0{{ index + 1 }}</small>
                <h3>{{ item.title }}</h3>
                <p>{{ item.text }}</p>
              </article>
            }
          </div>
        </section>
      }

      @if (capability().proof; as proof) {
        <section class="product-proof">
          <header>
            <p>{{ proof.eyebrow }}</p>
            <h2>{{ proof.title }}</h2>
            <span>{{ proof.lead }}</span>
          </header>
          <div class="proof-stage">
            <figure>
              <div class="browser-bar">
                <i></i><i></i><i></i><span>{{ proof.alt }}</span>
              </div>
              <img [src]="proof.image" [alt]="proof.alt" loading="lazy" decoding="async" />
            </figure>
            <aside>
              <small>AYMUDO / LIVE CONTROL</small>
              @for (point of proof.points; track point; let index = $index) {
                <article>
                  <b>0{{ index + 1 }}</b
                  ><span>{{ point }}</span
                  ><i>{{ index + 1 === proof.points.length ? '✓' : '→' }}</i>
                </article>
              }
            </aside>
          </div>
        </section>
      }

      <section class="flow" id="nasil-calisir">
        <header>
          <p>{{ labels().workingSystem }}</p>
          <h2>{{ labels().oneFlow }}</h2>
        </header>
        <ol>
          @for (step of capability().flow; track step; let index = $index) {
            <li>
              <span>0{{ index + 1 }}</span
              ><strong>{{ step }}</strong
              ><i></i><b>{{ index + 1 === capability().flow.length ? '✓' : '↓' }}</b>
            </li>
          }
        </ol>
      </section>

      <section class="ecosystem">
        <header>
          <p>{{ context().eyebrow }}</p>
          <h2>{{ context().title }}</h2>
          <span>{{ context().lead }}</span>
        </header>
        <div>
          @for (item of context().items; track item.title; let index = $index) {
            <article>
              <small>0{{ index + 1 }}</small>
              <h3>{{ item.title }}</h3>
              <p>{{ item.text }}</p>
              <b>CONNECTED</b>
            </article>
          }
        </div>
      </section>

      <section class="outcomes">
        <header>
          <p>{{ labels().businessResult }}</p>
          <h2>{{ labels().notFeature }}</h2>
        </header>
        <div>
          @for (outcome of capability().outcomes; track outcome; let index = $index) {
            <article>
              <span>0{{ index + 1 }}</span
              ><strong>{{ outcome }}</strong>
            </article>
          }
        </div>
      </section>

      <section class="related">
        <p>{{ labels().connected }}</p>
        <h2>{{ labels().wholeBusiness }}</h2>
        <nav>
          <a [routerLink]="i18n.routePath('products')">{{ labels().allProducts }} <b>→</b></a
          ><a [routerLink]="i18n.routePath('solutions')">{{ labels().solutions }} <b>→</b></a>
        </nav>
      </section>

      <section class="subscribe">
        <p>{{ labels().start }}</p>
        <h2>{{ labels().close }}</h2>
        <a [routerLink]="i18n.routePath('subscribe')">{{ labels().subscribe }} <b>↗</b></a>
      </section>
    </main>
  `,
  styleUrl: './aymudo-capability.page.scss',
})
export class AymudoCapabilityPage implements AfterViewInit, OnDestroy {
  protected readonly i18n = inject(I18nService);
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly slug = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('slug') ?? 'web-sitesi')),
    { initialValue: this.route.snapshot.paramMap.get('slug') ?? 'web-sitesi' },
  );
  protected readonly labels = computed(() => LABELS[this.i18n.contentLang()] ?? LABELS['en']);
  protected readonly capability = computed(() => {
    const language = this.i18n.contentLang();
    const source = CAPABILITIES[language] ?? CAPABILITIES['en'];
    return source[this.slug()] ?? source['web-sitesi'] ?? CAPABILITIES['en']['web-sitesi'];
  });
  protected readonly context = computed(() => {
    const language = this.i18n.contentLang();
    const contexts = CAPABILITY_CONTEXTS[language] ?? CAPABILITY_CONTEXTS['en'];
    return contexts[this.capability().scene] ?? contexts['site'];
  });
  protected readonly mailboxStory = computed(
    () => MAILBOX_STORIES[this.i18n.contentLang()] ?? MAILBOX_STORIES['en'],
  );
  private gsapContext?: { revert(): void };

  constructor() {
    effect(() => {
      const item = this.capability();
      this.seo.setPage({
        title: this.i18n.contentLang() === 'tr'
          ? `${item.title} işletme yönetimi yazılımı | Aymudo`
          : `${item.title} business management software | Aymudo`,
        description: item.lead,
        path: `/urunler/${this.slug()}`,
      });
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (
      typeof window === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;
    const [{ gsap }, { ScrollTrigger }] = await Promise.all([
      import('gsap'),
      import('gsap/ScrollTrigger'),
    ]);
    gsap.registerPlugin(ScrollTrigger);
    this.gsapContext = gsap.context(() => {
      gsap.from('.hero-copy>*', {
        y: 24,
        stagger: 0.07,
        duration: 0.65,
        ease: 'power3.out',
        clearProps: 'transform',
      });
      gsap.from('.system-scene article', {
        x: 38,
        stagger: 0.08,
        duration: 0.62,
        ease: 'power3.out',
        clearProps: 'transform',
      });
      gsap.utils
        .toArray<HTMLElement>(
          '.mailbox-channels article,.inbox-window,.mailbox-operations article,.product-proof,.flow li,.ecosystem article,.outcomes article',
        )
        .forEach((item) =>
          gsap.from(item, {
            y: 28,
            duration: 0.55,
            ease: 'power2.out',
            scrollTrigger: { trigger: item, start: 'top 92%', once: true },
            clearProps: 'transform',
          }),
        );
    }, this.host.nativeElement);
  }
  ngOnDestroy(): void {
    this.gsapContext?.revert();
  }
}

const LABELS: Record<string, any> = {
  tr: {
    products: 'Ürünler',
    subscribe: 'Aymudo’ya abone ol',
    seeHow: 'Nasıl çalıştığını gör',
    oneCore: 'TEK ALTYAPI',
    workingSystem: 'Birlikte çalışan sistem',
    oneFlow: 'Sinyalden sonuca tek akış.',
    businessResult: 'İşletme sonucu',
    notFeature: 'Yalnızca özellik değil, çalışan bir düzen.',
    connected: 'Aymudo ürünleri birbirinden kopuk değildir',
    wholeBusiness: 'Bu ürün, işletmenin geri kalanıyla aynı veriyi kullanır.',
    allProducts: 'Tüm ürünler',
    solutions: 'İşletmene uygun çözümler',
    start: 'İhtiyacın olan yerden başla',
    close: 'Bugün kullanın. Yarın aynı sistemde büyüyün.',
  },
  en: {
    products: 'Products',
    subscribe: 'Subscribe to Aymudo',
    seeHow: 'See how it works',
    oneCore: 'ONE CORE',
    workingSystem: 'A connected system',
    oneFlow: 'One flow from signal to result.',
    businessResult: 'Business outcome',
    notFeature: 'Not another feature. A working operation.',
    connected: 'Aymudo products do not live in silos',
    wholeBusiness: 'This product uses the same data as the rest of your business.',
    allProducts: 'All products',
    solutions: 'Solutions for your business',
    start: 'Start where you need',
    close: 'Use it today. Grow on the same system tomorrow.',
  },
};

const CAPABILITY_CONTEXTS: Record<string, Record<string, CapabilityContext>> = {
  tr: {
    site: {
      eyebrow: 'Birlikte çalışan yapı',
      title: 'Görünen yüz ile işletme sistemi birbirinden kopmaz.',
      lead: 'İçerik değiştiğinde marka korunur; müşteri harekete geçtiğinde işlem doğru Aymudo ürününe devam eder.',
      items: [
        {
          title: 'Tema ve içerik',
          text: 'Tasarım dili korunurken metin, görsel, sayfa ve bölümler ekip tarafından yönetilir.',
        },
        {
          title: 'Müşteri hareketleri',
          text: 'Form, randevu, ürün veya mesaj aynı müşteri kaydı ve izinli operasyon akışıyla buluşur.',
        },
        {
          title: 'Yayın ve ölçüm',
          text: 'Masaüstü, mobil, dil, alan adı ve performans tek yayın düzeninin parçaları olarak izlenir.',
        },
      ],
    },
    content: {
      eyebrow: 'İçerikten operasyona',
      title: 'Yayınlanan içerik yalnızca okunmaz; işletmeyi harekete geçirir.',
      lead: 'Bir rehber randevuya, ürün sayfası siparişe, kampanya ise ölçülebilir müşteri hareketine bağlanır.',
      items: [
        {
          title: 'Marka kütüphanesi',
          text: 'Metin, medya ve tekrar kullanılan bölümler tek bir düzen içinde güncel kalır.',
        },
        {
          title: 'Ekip yayını',
          text: 'Hazırlama, kontrol ve yayın adımları kişisel dosyalar yerine ortak çalışma alanında ilerler.',
        },
        {
          title: 'Sonuca bağlı içerik',
          text: 'İçerikten gelen trafik randevu, satış, form ve müşteri geçmişiyle birlikte okunur.',
        },
      ],
    },
    global: {
      eyebrow: 'Yerel deneyim, ortak merkez',
      title: 'Her pazar farklı görünür; operasyon aynı merkezde kalır.',
      lead: 'Dil ve para birimi vitrini yerelleştirirken depo, kargo, müşteri ve raporlama tek omurgada çalışır.',
      items: [
        {
          title: 'Dil ve RTL',
          text: '15 dil, Arapça yön sistemi ve yerelleştirilmiş rotalar aynı site üzerinde yönetilir.',
        },
        {
          title: 'Fiyat ve ödeme',
          text: 'Yaklaşık 30 para birimi, güncel kur ve pazara göre fiyat gösterimi birlikte çalışır.',
        },
        {
          title: 'Depo ve teslimat',
          text: 'Ülke, depo ve kargo profili siparişin doğru kaynaktan doğru yöntemle ilerlemesini sağlar.',
        },
      ],
    },
    team: {
      eyebrow: 'Zaman, ekip ve kaynak',
      title: 'Planlanan iş, doğru kişiye ve doğru çalışma alanına ulaşır.',
      lead: 'Şube, personel, hizmet ve uygunluk aynı organizasyon kaydını kullandığı için günlük operasyon dağılmaz.',
      items: [
        {
          title: 'Şube yapısı',
          text: 'Lokasyon, çalışma saatleri, hizmetler ve kaynaklar merkezi yapı içinde ayrı ayrı yönetilir.',
        },
        {
          title: 'Personel kimliği',
          text: 'Tek staff kaydı birden fazla şube veya işletmeyle kontrollü biçimde çalışabilir.',
        },
        {
          title: 'Bildirim ve takip',
          text: 'Randevu veya görev ilgili ekrana düşer; durum değişiklikleri müşteri ve yönetime yansır.',
        },
      ],
    },
    permission: {
      eyebrow: 'Yetki ile sadeleşen iş',
      title: 'Karmaşık sistem, personelin karşısına karmaşık çıkmaz.',
      lead: 'Her rol yalnızca ihtiyaç duyduğu veriyi, işlemi ve çalışma ekranını görür; yönetim bütün resmi korur.',
      items: [
        {
          title: 'Rol bazlı ekran',
          text: 'Kasiyer, uzman, şube yöneticisi veya içerik ekibi farklı çalışma yüzleri kullanır.',
        },
        {
          title: 'Veri sınırları',
          text: 'Şube, müşteri, rapor ve finans verileri role göre görüntülenir veya gizlenir.',
        },
        {
          title: 'Tek kimlik',
          text: 'Çalışan yeni bir işletmeye bağlandığında ayrı hesap açmadan yetkilendirilebilir.',
        },
      ],
    },
    mailbox: {
      eyebrow: 'Konuşmadan müşteri bağlamına',
      title: 'Mesaj, yorum ve ekip notları tek bir geçmişte buluşur.',
      lead: 'İletişim kişisel telefonlarda kalmaz; müşteri, sipariş, randevu veya satış bağlamıyla birlikte ilerler.',
      items: [
        {
          title: 'Ortak gelen kutusu',
          text: 'Yetkili kanallardan gelen konuşmalar tek listede toplanır ve kaybolmaz.',
        },
        {
          title: 'Ekip ataması',
          text: 'Konuşma doğru personele devredilir; cevap ve iç not geçmişi korunur.',
        },
        {
          title: 'İşleme dönüşen mesaj',
          text: 'Bir talep randevuya, siparişe, göreve veya takip akışına bağlanabilir.',
        },
      ],
    },
    stock: {
      eyebrow: 'Üründen teslimata',
      title: 'Satış kanalı değişir; ürün ve stok gerçeği değişmez.',
      lead: 'Web, sosyal satış, pazaryeri ve POS aynı ürün, varyasyon, depo ve sipariş kayıtlarıyla çalışır.',
      items: [
        {
          title: 'Ürün ve varyasyon',
          text: 'Fiyat, seçenek, barkod, medya ve politika bilgileri tek ürün kaydında tutulur.',
        },
        {
          title: 'Depo ve barkod',
          text: 'Çalışan telefondan barkod okutarak stok sorgular; hareket doğru depoya işlenir.',
        },
        {
          title: 'Sipariş ve kargo',
          text: 'Satış anında stok güncellenir, kargo profili seçilir ve müşteri süreci başlar.',
        },
      ],
    },
    pos: {
      eyebrow: 'Fiziksel ve dijital satış',
      title: 'Tezgâh, masa ve internet aynı ticaret sistemine bağlanır.',
      lead: 'Kasada yapılan işlem ayrı bir dünya oluşturmaz; müşteri, ürün, stok ve raporlama akışına katılır.',
      items: [
        {
          title: 'Adisyon ve sepet',
          text: 'Masa, müşteri veya hızlı satış için açılan kayıt ürün ve hizmetlerle birlikte yönetilir.',
        },
        {
          title: 'Ödeme ve kasa',
          text: 'Ödeme yöntemi, tahsilat ve kasa hareketi sipariş sonucu olarak kaydedilir.',
        },
        {
          title: 'Ortak stok',
          text: 'Fiziksel satış da web siparişi gibi ilgili depo ve ürün stoklarını anında günceller.',
        },
      ],
    },
    marketplace: {
      eyebrow: 'Platform ekonomisi',
      title: 'Satıcı deneyimi ile merkez yönetimi aynı pazarı işletir.',
      lead: 'Başvuru, yayın, sipariş, hizmet, komisyon ve ödeme tarafları ayrı panellerde fakat ortak kurallarla çalışır.',
      items: [
        {
          title: 'Satıcı ve onay',
          text: 'Başvurular incelenir, profil ve yetkiler açılır, satıcı kendi çalışma alanına kavuşur.',
        },
        {
          title: 'Ürün veya hizmet',
          text: 'Merkez yayın kurallarını korurken satıcı yetkili olduğu içeriği ve uygunluğu yönetir.',
        },
        {
          title: 'Komisyon ve ödeme',
          text: 'Sipariş sonucu satıcı payı, platform geliri ve ödeme durumu ölçülebilir kalır.',
        },
      ],
    },
    campaign: {
      eyebrow: 'Büyüme döngüsü',
      title: 'Kampanya yalnızca yayınlanmaz; satış sonucuna kadar izlenir.',
      lead: 'Hedef kitle, teklif, kanal ve dönüşüm aynı müşteri ve ticaret verisiyle değerlendirilir.',
      items: [
        {
          title: 'Kitle ve teklif',
          text: 'Müşteri davranışı, ürün veya hizmet bağlamına göre doğru kampanya kurgulanır.',
        },
        {
          title: 'Kanal ve içerik',
          text: 'Website, sosyal medya ve reklam anlatısı aynı teklif ve marka düzenini kullanır.',
        },
        {
          title: 'Dönüşüm ve rapor',
          text: 'Tıklama tek başına başarı sayılmaz; randevu, sipariş ve gelir sonucuyla birlikte ölçülür.',
        },
      ],
    },
    automation: {
      eyebrow: 'Olaydan otomasyona',
      title: 'Tekrarlanan kararlar, kontrollü iş akışlarına dönüşür.',
      lead: 'Site, satış ve operasyon genelindeki olaylar koşulları değerlendirir ve doğru işlemi otomatik başlatır.',
      items: [
        {
          title: 'Olay',
          text: 'Sipariş, stok, form, randevu, mesaj veya personel hareketi akışı başlatabilir.',
        },
        {
          title: 'Koşul',
          text: 'Şube, tutar, durum, müşteri, ürün veya zaman bilgisine göre karar verilir.',
        },
        {
          title: 'Eylem',
          text: 'Bildirim, görev, mesaj, atama veya durum güncellemesi izlenebilir biçimde uygulanır.',
        },
      ],
    },
  },
  en: {
    site: {
      eyebrow: 'A connected structure',
      title: 'The visible brand and the operating system stay connected.',
      lead: 'Content remains manageable and every customer action continues into the right Aymudo workflow.',
      items: [
        {
          title: 'Theme and content',
          text: 'Teams edit pages and media without losing the visual language of the brand.',
        },
        {
          title: 'Customer actions',
          text: 'Forms, bookings, products and messages meet one customer and operation context.',
        },
        {
          title: 'Publishing and insight',
          text: 'Desktop, mobile, language, domain and performance remain parts of one release system.',
        },
      ],
    },
    content: {
      eyebrow: 'From content to operation',
      title: 'Published content can move the business forward.',
      lead: 'A guide can become a booking, a product page can become an order and a campaign becomes measurable action.',
      items: [
        {
          title: 'Brand library',
          text: 'Copy, media and reusable sections remain current in one system.',
        },
        {
          title: 'Team publishing',
          text: 'Draft, review and release move through a shared workspace.',
        },
        {
          title: 'Content tied to results',
          text: 'Traffic connects with bookings, sales, forms and customer history.',
        },
      ],
    },
    global: {
      eyebrow: 'Local experience, shared centre',
      title: 'Every market can feel local while operations remain central.',
      lead: 'Language and currency localise the storefront while warehouse, delivery and reporting share one core.',
      items: [
        {
          title: 'Language and RTL',
          text: 'Fifteen languages, Arabic direction and localised routes work on one site.',
        },
        {
          title: 'Pricing and payment',
          text: 'Nearly thirty currencies, live rates and market pricing work together.',
        },
        {
          title: 'Warehouse and delivery',
          text: 'Country, warehouse and shipping profiles route every order correctly.',
        },
      ],
    },
    team: {
      eyebrow: 'Time, team and resources',
      title: 'Planned work reaches the right person and workspace.',
      lead: 'Branch, staff, service and availability use the same organisation record.',
      items: [
        {
          title: 'Branch structure',
          text: 'Locations, hours, services and resources remain centrally manageable.',
        },
        {
          title: 'Staff identity',
          text: 'One staff record can connect securely to multiple branches or businesses.',
        },
        {
          title: 'Notification and follow-up',
          text: 'Bookings and tasks reach the right workspace and update every party.',
        },
      ],
    },
    permission: {
      eyebrow: 'Work simplified by permissions',
      title: 'A complex system does not need to feel complex to staff.',
      lead: 'Each role sees only the data, actions and workspace required for the job.',
      items: [
        {
          title: 'Role-based workspace',
          text: 'Every operational role receives a focused working surface.',
        },
        {
          title: 'Data boundaries',
          text: 'Branch, customer, report and finance access follows permission rules.',
        },
        {
          title: 'One identity',
          text: 'Staff can join another business without creating another account.',
        },
      ],
    },
    mailbox: {
      eyebrow: 'From conversation to context',
      title: 'Messages, comments and internal notes share one history.',
      lead: 'Communication stays with the customer, order, booking or sales context.',
      items: [
        {
          title: 'Shared inbox',
          text: 'Authorised channels collect conversations in one durable list.',
        },
        {
          title: 'Team assignment',
          text: 'Conversations move between staff without losing replies or notes.',
        },
        {
          title: 'Message to action',
          text: 'A request can become a booking, order, task or follow-up.',
        },
      ],
    },
    stock: {
      eyebrow: 'From product to delivery',
      title: 'The channel changes. The product and inventory truth does not.',
      lead: 'Web, social selling, marketplaces and POS share products, variants, warehouses and orders.',
      items: [
        {
          title: 'Product and variant',
          text: 'Price, options, barcode, media and policies stay in one record.',
        },
        {
          title: 'Warehouse and barcode',
          text: 'Staff can scan with a phone and post movement to the right warehouse.',
        },
        {
          title: 'Order and shipping',
          text: 'A sale updates inventory, selects delivery and starts customer communication.',
        },
      ],
    },
    pos: {
      eyebrow: 'Physical and digital selling',
      title: 'Counter, table and online orders join the same commerce system.',
      lead: 'A till transaction joins customer, product, inventory and reporting context.',
      items: [
        {
          title: 'Tab and cart',
          text: 'Table, customer and quick sales use the same product and service records.',
        },
        {
          title: 'Payment and till',
          text: 'Payment method, collection and cash movement become an order result.',
        },
        {
          title: 'Shared inventory',
          text: 'Physical sales update the same inventory used by online channels.',
        },
      ],
    },
    marketplace: {
      eyebrow: 'Platform economy',
      title: 'Seller experience and central governance run the same market.',
      lead: 'Application, publishing, order, commission and payment use shared platform rules.',
      items: [
        {
          title: 'Seller and approval',
          text: 'Review applications, open profiles and provide a seller workspace.',
        },
        {
          title: 'Product or service',
          text: 'The platform protects rules while sellers manage authorised work.',
        },
        {
          title: 'Commission and payment',
          text: 'Seller share, platform revenue and settlement remain measurable.',
        },
      ],
    },
    campaign: {
      eyebrow: 'Growth loop',
      title: 'A campaign is measured through to the business result.',
      lead: 'Audience, offer, channel and conversion connect with customer and commerce data.',
      items: [
        {
          title: 'Audience and offer',
          text: 'Build the right offer from customer, product or service context.',
        },
        {
          title: 'Channel and content',
          text: 'Site, social and advertising use one offer and brand system.',
        },
        {
          title: 'Conversion and reporting',
          text: 'Measure bookings, orders and revenue rather than clicks alone.',
        },
      ],
    },
    automation: {
      eyebrow: 'From event to automation',
      title: 'Repeated decisions become controlled workflows.',
      lead: 'Events across site, sales and operations evaluate conditions and start the right action.',
      items: [
        {
          title: 'Event',
          text: 'An order, booking, form, message or inventory change can begin a flow.',
        },
        { title: 'Condition', text: 'Decide by branch, amount, state, customer, product or time.' },
        {
          title: 'Action',
          text: 'Apply notifications, tasks, assignments and state changes with a trace.',
        },
      ],
    },
  },
};

const TR: Record<string, Capability> = {
  'web-sitesi': {
    eyebrow: 'İşletmeni kur',
    title: 'Web sitesi ve yayın sistemi',
    promise: 'Markanız yayına çıkar. Sisteminiz arkasında çalışır.',
    lead: 'Sayfaları, içerikleri, alan adını ve yayın akışını parçalı araçlara ihtiyaç duymadan yönetin.',
    flow: [
      'Marka ve alan adını bağla',
      'Sayfaları ve içerikleri oluştur',
      'Masaüstü ve mobil deneyimi kontrol et',
      'Tek merkezden yayınla',
    ],
    outcomes: [
      'Kendi markanızla yayın',
      'Ekip tarafından yönetilebilen içerik',
      'Her cihazda tutarlı deneyim',
      'Diğer Aymudo ürünleriyle ortak veri',
    ],
    scene: 'site',
  },
  'tema-motoru': {
    eyebrow: 'Markanı görünür kıl',
    title: 'Tema motoru',
    promise: 'Altyapı ortak kalır. Görünüm yalnızca size ait olur.',
    lead: 'Renkten tipografiye, bölüm düzeninden görsellere kadar sitenizi kodla uğraşmadan yönetin; işletmenize özel deneyimi koruyun.',
    flow: [
      'Temayı seç',
      'Marka ayarlarını uygula',
      'Bölümleri ve medyayı düzenle',
      'Önizle ve yayınla',
    ],
    outcomes: [
      'Markaya özel tasarım',
      'Panelden kolay içerik yönetimi',
      'Masaüstü ve mobil tutarlılık',
      'Altyapı güncellemelerini kaybetmeyen tema',
    ],
    scene: 'site',
    proof: {
      eyebrow: 'Gerçek tema, gerçek işletme',
      title: 'Tasarım size özel kalır. Yönetimi ekibiniz yapar.',
      lead: 'Umut Demir temasındaki metinler, görseller, hizmet alanları ve sıralama panelden değiştirilebilir; markanın tasarım dili bozulmaz.',
      image: '/marketing/aymudo/screens/umutdemir-desktop.webp',
      alt: 'Umut Demir — Aymudo tema motoru',
      points: [
        'Görseli panelden değiştir',
        'Bölümleri sırala veya kapat',
        'Metin ve aksiyonları güncelle',
        'Masaüstü ve mobili birlikte önizle',
      ],
    },
  },
  'icerik-yayin': {
    eyebrow: 'İçerik ve blog',
    title: 'İçerik yönetimi',
    promise: 'Hikâyeniz, ürününüz ve güncellemeleriniz aynı yayın akışında.',
    lead: 'Blogları, rehberleri, sayfa bölümlerini ve medyayı ekipçe hazırlayın; doğru zamanda yayınlayın.',
    flow: [
      'İçeriği hazırla',
      'Medya ve bölümleri düzenle',
      'Ekip kontrolünü tamamla',
      'Yayınla ve güncelle',
    ],
    outcomes: [
      'Tek içerik merkezi',
      'Daha hızlı yayın süreci',
      'Marka genelinde tutarlılık',
      'Arama motoruna hazır yapı',
    ],
    scene: 'content',
  },
  yerellestirme: {
    eyebrow: 'Dünyaya açıl',
    title: 'Dil ve ülke yapılandırması',
    promise: 'Aynı marka, her pazarda yerel bir deneyim.',
    lead: 'Dilleri, RTL yönünü, ülkeleri ve para birimlerini tek sitede doğru ziyaretçiye sunun.',
    flow: [
      'Pazarları seç',
      'Dil ve RTL yapısını tanımla',
      'Ülke ve para birimini bağla',
      'Yerel deneyimi yayınla',
    ],
    outcomes: [
      '15 dil desteği',
      'Arapça ve RTL deneyimi',
      'Ülkeye göre içerik',
      'Yaklaşık 30 güncel para birimi',
    ],
    scene: 'global',
  },
  'sube-personel': {
    eyebrow: 'İşletmeni yönet',
    title: 'Şube ve personel',
    promise: 'Bir işletme büyür. Yönetim dağılmak zorunda değildir.',
    lead: 'Şubeleri, ekipleri, hizmetleri ve kaynakları aynı organizasyon yapısında yönetin.',
    flow: [
      'Şubeyi oluştur',
      'Personeli ve kaynağı bağla',
      'Rol ve çalışma düzenini tanımla',
      'Tüm operasyonu tek panelde izle',
    ],
    outcomes: [
      'Çok şubeli tek yönetim',
      'Tek staff kimliği',
      'Şubeye göre yetki',
      'Merkezi operasyon görünümü',
    ],
    scene: 'team',
  },
  randevu: {
    eyebrow: 'Zamanı ve hizmeti yönet',
    title: 'Randevular',
    promise: 'Uygun saat, doğru personel ve gereken kaynak aynı anda buluşur.',
    lead: 'Hizmet sürelerini, ekip takvimlerini, şubeleri ve kaynak uygunluğunu tek rezervasyon akışında yönetin.',
    flow: [
      'Hizmet ve süreyi tanımla',
      'Personel ve kaynağı bağla',
      'Uygun saatleri gerçek zamanlı aç',
      'Randevuyu ekibe ve müşteriye ilet',
    ],
    outcomes: [
      'Çakışmasız takvim',
      'Gerçek zamanlı uygunluk',
      'Şube ve personele göre planlama',
      'Müşteri için kısa rezervasyon akışı',
    ],
    scene: 'team',
    proof: {
      eyebrow: 'Randevu, sitenin doğal bir parçası',
      title: 'Klinikten kuaföre aynı motor, işletmeye özel deneyim.',
      lead: 'CEON Dent üzerinde çalışan randevu altyapısı; şube, doktor, hizmet, süre ve gerçek zamanlı uygunluğu aynı akışta kullanır.',
      image: '/marketing/aymudo/screens/ceondent-desktop.webp',
      alt: 'CEON Dent — Aymudo randevu sistemi',
      points: [
        'Şubeyi ve hizmeti bağla',
        'Personel takvimini eşleştir',
        'Kaynak çakışmasını önle',
        'Randevuyu anında ekibe ilet',
      ],
    },
  },
  'ekran-yetki': {
    eyebrow: 'Doğru iş, doğru ekran',
    title: 'Personel ekranları ve yetkiler',
    promise: 'Herkes tüm sistemi değil, işini görür.',
    lead: 'Her personele yalnızca rolü için gereken ekranı, veriyi ve işlem yetkisini atayın.',
    flow: [
      'Rolü seç',
      'Çalışma ekranını ata',
      'Veri ve işlem yetkisini sınırla',
      'Ekip değiştikçe güncelle',
    ],
    outcomes: [
      'Daha sade personel deneyimi',
      'Kontrollü veri erişimi',
      'Daha az operasyon hatası',
      'Birden fazla işletmede tek kimlik',
    ],
    scene: 'permission',
  },
  mailbox: {
    eyebrow: 'Ortak iletişim',
    title: 'Mailbox ve Konuşmalar',
    promise: 'Bütün mesajlar tek yerde. Her konuşma doğru bağlamda.',
    lead: 'E-postaları, destek taleplerini, sosyal medya mesajlarını ve sistem olaylarını ortak gelen kutusunda ekipçe yönetin.',
    flow: [
      'E-posta ve mesaj kanallarını bağla',
      'Konuşmayı müşteri geçmişiyle birleştir',
      'Doğru ekip veya şubeye ata',
      'Yanıtla, çöz ve bağlamı koru',
    ],
    outcomes: [
      'Tek gelen kutusu',
      'Ekip içinde devir kolaylığı',
      'Müşteri bağlamının korunması',
      'Daha hızlı yanıt',
    ],
    scene: 'mailbox',
  },
  'stok-depo': {
    eyebrow: 'Her yerde sat',
    title: 'Stok ve depo',
    promise: 'Ürün nerede olursa olsun, stok tek gerçeği söyler.',
    lead: 'Ürünleri, varyasyonları, depoları ve barkod hareketlerini satış kanallarıyla birlikte izleyin.',
    flow: [
      'Ürün ve varyasyonu tanımla',
      'Depolara stok ata',
      'Barkodla sorgula veya hareket ettir',
      'Satışla birlikte stok güncelle',
    ],
    outcomes: [
      'Çoklu depo görünümü',
      'Telefon kamerasıyla barkod sorgusu',
      'Kanal bazlı doğru stok',
      'Daha az stok hatası',
    ],
    scene: 'stock',
  },
  'e-ticaret': {
    eyebrow: 'Her yerde sat',
    title: 'E-ticaret',
    promise: 'Ürün vitrinden teslimata kadar tek sipariş akışında ilerler.',
    lead: 'Ürün, varyasyon, sepet, ödeme, stok, kargo ve müşteri iletişimini birbirinden koparmadan yönetin.',
    flow: [
      'Ürünleri ve fiyatları hazırla',
      'Vitrini ve ödeme akışını yayınla',
      'Siparişi stok ve depoya bağla',
      'Kargoyu ve müşteriyi güncelle',
    ],
    outcomes: [
      'Tek ticaret merkezi',
      'Siparişle birlikte güncellenen stok',
      'Kargo profiline göre teslimat',
      'Müşteri geçmişiyle bağlı satış',
    ],
    scene: 'stock',
    proof: {
      eyebrow: 'Global mağaza kanıtı',
      title: 'Bir ürün kataloğundan fazlası: çalışan uluslararası ticaret.',
      lead: 'BDSMBeds; ürün, varyasyon, para birimi, kargo ve sipariş akışını Aymudo üzerinde tek global mağazada çalıştırır.',
      image: '/marketing/aymudo/screens/bdsmbeds-desktop.webp',
      alt: 'BDSMBeds — Aymudo e-ticaret sistemi',
      points: [
        'Ürün ve varyasyonları yönet',
        'Ülkeye göre fiyat göster',
        'Depo ve kargo profilini seç',
        'Siparişi tek merkezde takip et',
      ],
    },
  },
  'pos-kasa': {
    eyebrow: 'Fiziksel satış',
    title: 'Aymudo POS, adisyon ve kasa',
    promise: 'Kasadaki satış, internet siparişinden ayrı yaşamaz.',
    lead: 'Fiziksel satışı, adisyonu, ödemeyi ve stok hareketini Aymudo sipariş sistemiyle birleştirin.',
    flow: [
      'Ürün veya adisyonu aç',
      'Ödemeyi al',
      'Sipariş ve kasayı kaydet',
      'Stoku anında güncelle',
    ],
    outcomes: [
      'Tek satış kaydı',
      'Anlık kasa görünümü',
      'Adisyon ve ödeme takibi',
      'Online ve fiziksel ortak stok',
    ],
    scene: 'pos',
  },
  pazaryeri: {
    eyebrow: 'Platform kur',
    title: 'Pazaryeri ve çoklu satıcı',
    promise: 'Satıcıdan komisyona kadar bütün pazar tek sistemde.',
    lead: 'Satıcı kayıtlarını, ürünleri, siparişleri, komisyonları ve ödemeleri merkezi olarak yönetin.',
    flow: [
      'Satıcıyı sisteme al',
      'Ürün ve yayın kurallarını uygula',
      'Siparişleri satıcıya dağıt',
      'Komisyon ve ödemeyi takip et',
    ],
    outcomes: [
      'Çoklu satıcı altyapısı',
      'Merkezi ürün kontrolü',
      'Sipariş dağıtımı',
      'Şeffaf komisyon yönetimi',
    ],
    scene: 'marketplace',
    proof: {
      eyebrow: 'Canlı hizmet pazaryeri',
      title: 'Ürün değil, uzman ve zaman satan pazarlar da aynı merkezde.',
      lead: 'Fal Deryası; satıcı profilleri, çevrimiçi durum, puanlar, hizmetler ve canlı talep akışını Aymudo pazaryeri üzerinde birleştirir.',
      image: '/marketing/aymudo/screens/falderyasi-desktop.webp',
      alt: 'Fal Deryası — Aymudo hizmet pazaryeri',
      points: [
        'Satıcı başvurularını yönet',
        'Canlı durum ve hizmetleri yayınla',
        'Talebi doğru satıcıya dağıt',
        'Komisyon ve ödemeyi izle',
      ],
    },
  },
  'kampanya-reklam': {
    eyebrow: 'İşletmeni büyüt',
    title: 'Kampanya ve reklam yönetimi',
    promise: 'Teklif, hedef kitle ve sonuç aynı büyüme hikâyesinde.',
    lead: 'Kampanyaları, indirimleri ve reklam çalışmalarını müşteri ve satış verisiyle birlikte yönetin.',
    flow: [
      'Hedefi ve kitleyi seç',
      'Teklif veya kampanyayı kur',
      'Kanallarda yayınla',
      'Sonucu satışla birlikte ölç',
    ],
    outcomes: [
      'Merkezi kampanya yönetimi',
      'Müşteri verisiyle doğru hedefleme',
      'Kanallar arası tutarlılık',
      'Satışa bağlı ölçüm',
    ],
    scene: 'campaign',
  },
  'sosyal-medya': {
    eyebrow: 'Kanalları tek merkezde yönet',
    title: 'Sosyal medya yönetimi',
    promise: 'Gönderi, yorum, mesaj ve satış aynı müşteri hikâyesinde kalır.',
    lead: 'Yetkili hesaplardan içerik yayınlayın, konuşmaları ortak gelen kutusunda yönetin ve sosyal satışları işletme verisine bağlayın.',
    flow: [
      'Sosyal hesapları bağla',
      'İçeriği hazırla ve planla',
      'Mesaj ve yorumları ekibe dağıt',
      'Etkileşimi müşteri ve satışla ölç',
    ],
    outcomes: [
      'Tek yayın takvimi',
      'Ortak mesaj ve yorum yönetimi',
      'Yetkili ekip akışı',
      'Sosyal satışla bağlı müşteri geçmişi',
    ],
    scene: 'mailbox',
  },
  tetikleyiciler: {
    eyebrow: 'Site genelinde otomasyon',
    title: 'Tetikleyiciler',
    promise: 'Bir olay olur. Aymudo doğru işi kendisi başlatır.',
    lead: 'Yalnızca sosyal medyada değil; site, satış ve operasyon genelinde koşula bağlı akışlar oluşturun.',
    flow: ['Olayı seç', 'Koşulları tanımla', 'Yapılacak işlemi bağla', 'Akışı izle ve geliştir'],
    outcomes: [
      'Tekrarlanan işlerde zaman tasarrufu',
      'Site genelinde otomasyon',
      'Tutarlı müşteri iletişimi',
      'Ölçeklenebilir operasyon',
    ],
    scene: 'automation',
  },
  'global-satis': {
    eyebrow: 'Dünyaya açıl',
    title: 'Global satış',
    promise: 'Yeni ülke, yeni sistem demek değildir.',
    lead: 'Dil, para birimi, ülke, depo, kargo ve yerel deneyimi tek global satış yapısında çalıştırın.',
    flow: [
      'Ülke ve dili aç',
      'Para birimi ve fiyatı bağla',
      'Depo ve kargo profilini seç',
      'Yerel deneyimle satışa başla',
    ],
    outcomes: [
      '15 dil ve RTL',
      'Yaklaşık 30 para birimi',
      'Çoklu depo ve kargo profili',
      'Tek merkezden dünya satışı',
    ],
    scene: 'global',
  },
};

const EN: Record<string, Capability> = Object.fromEntries(
  Object.entries(TR).map(([slug, item]) => [
    slug,
    {
      ...item,
      eyebrow: 'Aymudo product',
      lead: 'A connected Aymudo capability designed to work with the rest of your business.',
      flow: [
        'Configure the operation',
        'Connect people and data',
        'Run the live workflow',
        'Measure and improve',
      ],
      outcomes: [
        'One source of truth',
        'A clearer team workflow',
        'Connected customer context',
        'Ready to scale',
      ],
      proof: item.proof
        ? {
            ...item.proof,
            eyebrow: 'A live product proof',
            title: `${item.title}, running on a real business.`,
            lead: 'See the product working as part of a real brand and manage the same experience from one Aymudo workspace.',
            points: [
              'Manage content and settings',
              'Keep the brand experience intact',
              'Preview every device',
              'Publish from one workspace',
            ],
          }
        : undefined,
    },
  ]),
) as Record<string, Capability>;
const EN_TITLES: Record<string, Pick<Capability, 'title' | 'promise'>> = {
  'tema-motoru': { title: 'Theme engine', promise: 'One brand system, consistent on every screen.' },
  'web-sitesi': { title: 'Website builder', promise: 'Your website becomes an active part of your business.' },
  randevu: { title: 'Appointments', promise: 'The right time, staff member and resource meet in one booking.' },
  'sube-personel': { title: 'Branches and staff', promise: 'Your business can grow without fragmenting management.' },
  'ekran-yetki': { title: 'Staff screens and permissions', promise: 'Everyone sees the work and data their role requires.' },
  mailbox: { title: 'Mailbox and Conversations', promise: 'Every message in one place. Every conversation in context.' },
  'e-ticaret': { title: 'E-commerce', promise: 'Products, customers, payments and fulfilment run as one operation.' },
  'stok-depo': { title: 'Inventory and warehouses', promise: 'Know what is available, where it is and what needs action.' },
  'pos-kasa': { title: 'POS and checkout', promise: 'Every sale reaches the same customer, stock and payment record.' },
  pazaryeri: { title: 'Marketplace', promise: 'Bring multiple sellers and customers together on one managed platform.' },
  'sosyal-medya': { title: 'Social media management', promise: 'Plan, publish and follow customer responses from one workspace.' },
  'icerik-yayin': { title: 'Content publishing', promise: 'Create once, review clearly and publish with confidence.' },
  'kampanya-reklam': { title: 'Campaigns and advertising', promise: 'Connect advertising activity to conversations, bookings and sales.' },
  tetikleyiciler: { title: 'Triggers and automation', promise: 'When an event happens, Aymudo starts the right work.' },
  yerellestirme: { title: 'Localization', promise: 'Keep one brand while giving every market a local experience.' },
  'global-satis': { title: 'Global sales', promise: 'A new country does not require a new system.' },
};
for (const [slug, copy] of Object.entries(EN_TITLES)) {
  Object.assign(EN[slug], copy);
  if (EN[slug].proof) EN[slug].proof!.title = `${copy.title}, running on a real business.`;
}
EN['mailbox'] = {
  ...EN['mailbox'],
  title: 'Mailbox and Conversations',
  promise: 'Every message in one place. Every conversation in context.',
  lead: 'Manage email, support requests, social messages and system events together in one team inbox.',
  flow: [
    'Connect email and messaging channels',
    'Unify the conversation with customer history',
    'Assign the right team or branch',
    'Reply, resolve and preserve context',
  ],
};
const CAPABILITIES: Record<string, Record<string, Capability>> = { tr: TR, en: EN };

const MAILBOX_STORIES: Record<string, MailboxStory> = {
  tr: {
    eyebrow: 'Bütün kanallar · tek çalışma alanı',
    title: 'Müşterinin yazdığı yer değişir. Ekibin çalıştığı ekran değişmez.',
    lead: 'Mailbox farklı e-posta hesaplarını tek yerde toplar. Konuşmalar ise destek taleplerini, gelen e-postaları, sosyal medya mesajlarını ve sistem bildirimlerini müşteri bağlamıyla aynı ekrana taşır.',
    channels: [
      {
        label: 'E-posta',
        badge: '@',
        title: 'Tüm e-posta hesapları',
        text: 'Satış, destek, bilgi ve şube adreslerine gelen e-postaları hesap hesap dolaşmadan tek gelen kutusunda yönetin.',
      },
      {
        label: 'Destek',
        badge: '?',
        title: 'Sistem destek mesajları',
        text: 'Site içinden açılan destek talepleri gerçek zamanlı olarak aynı konuşma listesine düşer ve ekip içinde sahiplenilir.',
      },
      {
        label: 'Sosyal',
        badge: '#',
        title: 'Sosyal medya mesajları',
        text: 'Yetkilendirilmiş sosyal hesaplardan gelen mesaj ve yorumları müşteri konuşmasının devamı olarak takip edin.',
      },
      {
        label: 'Sistem',
        badge: '⚡',
        title: 'Olay ve bildirim akışı',
        text: 'Sipariş, randevu veya operasyon kaynaklı önemli sistem mesajlarını doğru konuşma ve ekip bağlamında görün.',
      },
    ],
    inboxEyebrow: 'Konuşmalar',
    inboxTitle: 'Kanal değil, müşteri odaklı bir gelen kutusu.',
    inboxLead:
      'Aynı müşteri e-posta gönderip destek talebi açtığında ekip iki ayrı yabancıyla konuşmaz. Geçmiş, ilgili sipariş veya randevu ve ekip notları aynı bağlamda kalır.',
    replyPlaceholder: 'Yanıt yaz…',
    send: 'Gönder',
    filters: ['Tüm konuşmalar', 'Bana atananlar', 'Yanıt bekleyen', 'Çözümlenen'],
    threadList: [
      {
        source: 'DESTEK · ŞİMDİ',
        name: 'Elif Kaya',
        preview: 'Randevumu başka bir saate alabilir miyiz?',
        state: 'YANIT BEKLİYOR',
      },
      {
        source: 'E-POSTA · 4 DK',
        name: 'Mert Tekstil',
        preview: 'Toplu sipariş için fiyat bilgisi rica ederiz.',
        state: 'SATIŞ',
      },
      {
        source: 'INSTAGRAM · 12 DK',
        name: 'Selin A.',
        preview: 'Bu ürünün farklı rengi var mı?',
        state: 'ATANDI',
      },
      {
        source: 'SİSTEM · 18 DK',
        name: 'Sipariş #1842',
        preview: 'Ödeme onaylandı, hazırlık başlayabilir.',
        state: 'OPERASYON',
      },
    ],
    activeThread: {
      customer: 'Elif Kaya',
      context: 'Müşteri · Randevu #218 · Göztepe şubesi',
      messages: [
        {
          sender: 'ELİF · CANLI DESTEK',
          text: 'Merhaba, yarınki randevumu başka bir saate alabilir miyiz?',
        },
        { sender: 'SİSTEM BAĞLAMI', text: 'Randevu #218 · 14:30 · Saç bakımı · Göztepe' },
        {
          sender: 'MURAT · DESTEK EKİBİ',
          text: 'Elbette. Aynı gün 16:00 için uygunluk görünüyor.',
          mine: true,
        },
      ],
    },
    operationsEyebrow: 'Ekip çalışma sistemi',
    operationsTitle: 'Yanıt vermekten fazlası: sahiplen, aktar, çöz ve hatırla.',
    operations: [
      {
        title: 'Doğru kişiye ata',
        text: 'Konuşmayı ilgili personele, departmana veya şubeye atayın; kimin ilgilendiği belirsiz kalmasın.',
      },
      {
        title: 'Durumu görünür tut',
        text: 'Yanıt bekleyen, işlemde ve çözümlenen konuşmaları ortak durumlarla takip edin.',
      },
      {
        title: 'Ekip notunu müşteriden ayır',
        text: 'Müşteriye görünmeyen iç notlarla ekip içinde bağlam aktarın ve devir sırasında bilgi kaybetmeyin.',
      },
      {
        title: 'Müşteri geçmişini koru',
        text: 'Mesajı yalnız metin olarak değil; müşteri, sipariş, randevu ve önceki görüşmelerle birlikte değerlendirin.',
      },
    ],
  },
  en: {
    eyebrow: 'Every channel · one workspace',
    title: 'The place customers write can change. The team workspace does not.',
    lead: 'Mailbox brings email accounts together. Conversations unifies support requests, incoming email, social messages and system notifications with customer context.',
    channels: [
      {
        label: 'Email',
        badge: '@',
        title: 'Every email account',
        text: 'Manage sales, support, information and branch inboxes without moving between separate accounts.',
      },
      {
        label: 'Support',
        badge: '?',
        title: 'System support messages',
        text: 'Requests opened on the site arrive in the same conversation list and can be owned by the right team.',
      },
      {
        label: 'Social',
        badge: '#',
        title: 'Social messages',
        text: 'Follow messages and comments from authorised social accounts as part of the customer conversation.',
      },
      {
        label: 'System',
        badge: '⚡',
        title: 'Events and notifications',
        text: 'See important order, booking and operational events inside the correct conversation context.',
      },
    ],
    inboxEyebrow: 'Conversations',
    inboxTitle: 'An inbox organised around customers, not channels.',
    inboxLead:
      'When the same customer emails and opens a support request, the team does not meet two strangers. History, related orders or bookings and internal notes remain together.',
    replyPlaceholder: 'Write a reply…',
    send: 'Send',
    filters: ['All conversations', 'Assigned to me', 'Awaiting reply', 'Resolved'],
    threadList: [
      {
        source: 'SUPPORT · NOW',
        name: 'Elif Kaya',
        preview: 'Could we move my appointment to another time?',
        state: 'AWAITING REPLY',
      },
      {
        source: 'EMAIL · 4 MIN',
        name: 'Mert Textile',
        preview: 'Could you share pricing for a bulk order?',
        state: 'SALES',
      },
      {
        source: 'INSTAGRAM · 12 MIN',
        name: 'Selin A.',
        preview: 'Is this product available in another colour?',
        state: 'ASSIGNED',
      },
      {
        source: 'SYSTEM · 18 MIN',
        name: 'Order #1842',
        preview: 'Payment approved. Preparation can begin.',
        state: 'OPERATIONS',
      },
    ],
    activeThread: {
      customer: 'Elif Kaya',
      context: 'Customer · Booking #218 · Göztepe branch',
      messages: [
        {
          sender: 'ELIF · LIVE SUPPORT',
          text: 'Hello, could we move my appointment tomorrow to another time?',
        },
        { sender: 'SYSTEM CONTEXT', text: 'Booking #218 · 14:30 · Hair treatment · Göztepe' },
        {
          sender: 'MURAT · SUPPORT TEAM',
          text: 'Of course. Availability is open at 16:00 on the same day.',
          mine: true,
        },
      ],
    },
    operationsEyebrow: 'Team workflow',
    operationsTitle: 'More than replying: own, hand over, resolve and remember.',
    operations: [
      {
        title: 'Assign the right owner',
        text: 'Send a conversation to the relevant person, department or branch so responsibility stays clear.',
      },
      {
        title: 'Keep status visible',
        text: 'Track awaiting reply, in progress and resolved conversations with shared states.',
      },
      {
        title: 'Separate internal notes',
        text: 'Pass context with notes customers cannot see and preserve information during a handover.',
      },
      {
        title: 'Keep customer history',
        text: 'Read the message together with the customer, order, booking and earlier conversations.',
      },
    ],
  },
};
